import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import IcMic from "@/assets/svg/ic_mic.svg?react";
import IcArrowDown from "@/assets/svg/ic_arrow_down.svg?react";
import Tip from "./Tip";
import ConnectModal from "./ConnectModal";
import MicSelectorModal from "./MicSelectorModal";
import {
  MicSwitcherProvider,
  initialState,
  localDevice,
  micSwitcherReducer,
} from "./context";
import { RuningInLink } from "@/constants";
import { toast } from "react-toastify";
import WifiHandler from "@/utils/wifiHandler";
import { AudioGetter, ScreenShareApis, WifiInfo } from "@core/ability";
import { ICustomeDevcie } from "@/types/device";
import IcMicrophone from "@/assets/svg/ic_microphone.svg?react";
import getLocalAudioType from "@/utils/getLocalAudioType";
import MeetingHandler from "@/bussiness/MeetingHandler";
import { formatCustomDeviceInfo, formatMicList } from "./util";
import { message } from "antd";

const audioGetter = new AudioGetter();

const INTERVAL_SCAN_TIME = 10 * 1000;
let localAudioType = "";

function MicSwitcher() {
  const [showTip, setShowTip] = useState(false);
  const timerRef = useRef<NodeJS.Timeout>();
  const [state, dispatch] = useReducer(micSwitcherReducer, initialState);
  const tipHasShowed = useRef<boolean>(false);
  const closeScanWifi = useRef<boolean>(false);
  const currentMicRef = useRef(state.currentMic);
  currentMicRef.current = state.currentMic;
  const currentDeviceRef = useRef(state.currentDevice);
  currentDeviceRef.current = state.currentDevice;
  const fetchLocalAudioType = async () => {
    const { type } = await getLocalAudioType();
    localAudioType = type;
  };
  useEffect(() => {
    fetchLocalAudioType();
  }, []);

  const handleScanWifi = useCallback(async () => {
    // 获取位置权限
    // const res = tryToGetGeolocationPersimission();
    // if (!res) {
    //   message.error("为了扫描附近设备，请允许定位权限失败");
    //   closeScanWifi.current = true;
    //   return;
    // } else {
    //   console.log('以允许定位权限检测');
    // }
    // 扫描wifi设备
    ScreenShareApis.scanDevices()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((res) => {
        if (!res.data.length) return;
        const wifiInfos: WifiInfo[] = Object.values(res.data);
        const wifiList = WifiHandler.filterWifiList(
          WifiHandler.sortWifiListWithRssi(wifiInfos)
        );
        // 把wifi列表转换为设备列表
        const devcieList: ICustomeDevcie[] = wifiList.map((wifiInfo) => {
          return formatCustomDeviceInfo(wifiInfo) as ICustomeDevcie;
        });
        dispatch({ type: "updateDeviceList", payload: devcieList });
        // 展示提示
        if (devcieList.length > 0 && !tipHasShowed.current) {
          setShowTip(true);
          tipHasShowed.current = true;
          timerRef.current = setTimeout(() => {
            setShowTip(false);
          }, 5000);
        }
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .catch((error: any) => {
        console.log("scanDevices error", error);
      });
  }, [dispatch]);

  const handleGetMicList = useCallback(
    async (setDefault: boolean) => {
      let defaultMic;
      let filterList;
      // 获取麦克风列表
      if (!localAudioType) {
        await fetchLocalAudioType();
      }
      if (localAudioType === "localLink") {
        const res = await ScreenShareApis.getAvailableMicrophones();
        if (res.code !== 0) {
          throw new Error(res.msg);
        }
        filterList = formatMicList(res.data);
        defaultMic = filterList.find((item) => item.isDefault);
      } else {
        const list = await audioGetter.getMicList();
        const defaultMicTmp = list.find((mic) =>
          mic.deviceId.startsWith("default")
        );
        filterList = list.filter((mic) => mic !== defaultMicTmp);
        const defaultMicLabel = defaultMicTmp?.label.split(" - ")[1];
        defaultMic = filterList.find((mic) => mic.label === defaultMicLabel) || filterList[0];
      }
      dispatch({
        type: "updateMicList",
        payload: filterList as MediaDeviceInfo[],
      });
      // 如果当前选中的不是本地音频，则退出
      if (currentDeviceRef.current?.type !== "computer") return;

      if (!setDefault) return;
      dispatch({
        type: "updateCurrentMic",
        payload: defaultMic as MediaDeviceInfo,
      });
      console.log("handleGetMicList", setDefault, defaultMic);
      // 第一次才显示默认麦克风toast
      toast.success(`正在使用"${defaultMic?.label}"拾音`, {
        icon: IcMicrophone,
      });
    },
    [dispatch]
  );

  const handleGetList = useCallback(
    (setDefault: boolean) => {
      if (RuningInLink && !closeScanWifi.current) {
        handleScanWifi();
      }
      handleGetMicList(setDefault);
    },
    [handleGetMicList, handleScanWifi]
  );

  // 第一次获取设备列表
  useEffect(() => {
    handleGetList(true);
  }, [handleGetList]);

  // 定时刷新设备列表
  useEffect(() => {
    // 每10s刷新一次设备列表
    if (!state.showMicModal && !state.showConnectModal) return;
    handleGetList(false);
    const timer = setInterval(() => {
      // console.log("interval scan wifi");
      handleGetList(false);
    }, INTERVAL_SCAN_TIME);
    return () => {
      timer && clearInterval(timer);
    };
  }, [handleGetList, state.showMicModal, state.showConnectModal]);

  useEffect(() => {
    const cb = async (error: Error) => {
      message.error(error.message);
      console.log(
        "listenDeviceConnectError设备连接中断，暂停录制，选择默认麦克风"
      );
      await MeetingHandler.pauseRecord();
      await MeetingHandler.switchDevice(localDevice);
      await MeetingHandler.switchMicrophone(
        currentMicRef.current as MediaDeviceInfo
      );
      dispatch({ type: "updateCurrentDevice", payload: localDevice });
    };
    const unListenDeviceDisconnected =
      MeetingHandler.listenDeviceDisconnected(cb);
    return () => {
      unListenDeviceDisconnected();
    };

  }, [handleGetList]);

  const handleShowSelector = () => {
    console.log(state.showConnectModal, state.showMicModal);
    if (state.showConnectModal) {
      dispatch({ type: "updateConnectModal", payload: false });
      return;
    }
    dispatch({ type: "updateMicModal", payload: !state.showMicModal });
    if (showTip) {
      setShowTip(false);
      timerRef.current && clearTimeout(timerRef.current);
    }
    // 显示弹窗时刷新列表
    if (state.showMicModal) {
      handleGetList(false);
    }
  };

  return (
    <div className="min-w-15 position-relative flex items-center b-#ECEEF3 b-r-solid b-r-1px pr-4">
      <MicSwitcherProvider value={{ state, dispatch }}>
        <div className="cursor-pointer ml-8" onClick={handleShowSelector}>
          <IcMic className="w-5 h-5" />
          <IcArrowDown className="w-4 h-4" />
        </div>
        {showTip && <Tip />}
        {state.showMicModal && <MicSelectorModal />}
        {state.showConnectModal && <ConnectModal />}
      </MicSwitcherProvider>
    </div>
  );
}

export default MicSwitcher;
